import React, { useMemo } from 'react';
import { type FieldHelperProps, useField } from 'formik';
import { ValidValueProps, useValidationFeedback } from '../../components/ValidationFeedbackFormik';
import TextInput from './TextInput';
import CheckBox from './CheckBox';
import RadioButton from './RadioButton';
import useDefaultId from '@n2-common/lib/useDefaultId';

export type UiState = 'standard' | 'value-verified';
export type UiStateProps = { uistate?: UiState };
export type BaseFieldProps = {
    name: string;
    label: string;
    error?: string | false;
    helptext?: string | undefined | JSX.Element | null;
};

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & BaseFieldProps & { helpers?: FieldHelperProps<unknown> };

export default function Input({ type, name, ...props }: InputProps & UiStateProps) {
    const { helpers, ...attr } = props;
    if (helpers && process.env.NODE_ENV === 'development') {
        attr.value === null &&
            console.warn(
                `Value of ${name} is null, and is bound via FormikInput initial values to ${name}.
                Use empty string on Formik initialvalues to clear the component.`
            );
    }

    switch (type) {
        case undefined:
            throw new Error('Property type is required on Input component.');

        case 'checkbox':
            return <CheckBox name={name} {...attr} />;

        case 'radio':
            return <RadioButton name={name} {...attr} />;

        default:
            return <TextInput name={name} type={type} {...attr} />;
    }
}

export function FormikInput({
    name,
    label,
    type,
    id,
    customError,
    onValidValue,
    ...attr
}: InputProps & ValidValueProps & { customError?: false | string }): JSX.Element {
    const compId = useDefaultId(id);
    const [fieldprops, { touched, error }, helpers] = useField({ name, type });
    const [verified, onInput] = useValidationFeedback(name, onValidValue);
    const uistate = useMemo(() => (verified && !customError ? 'value-verified' : 'standard'), [customError, verified]);

    return (
        <Input
            id={compId}
            uistate={uistate}
            onInput={onInput}
            error={touched && (error || customError)}
            label={label}
            type={type}
            helpers={helpers}
            {...fieldprops}
            {...attr}
        />
    );
}
